/**
 * Weekly Service Schedule — landscape A4 PDF (Research tab, Timetable Studio v4).
 *
 * Captures the whole schedule block: assumptions (sliders, cost rates, price
 * adjustments), the Mon–Sun calendar of modelled days (services steam/diesel,
 * passengers, revenue, costs, profit) with weekly totals, the monthly
 * overview, the per-day-type modelling summary and the modelled ticket
 * splits. Downloads directly (jsPDF + autotable, no popup — matches the P&L /
 * Bookings / Master-View pattern).
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import type { DayModel } from "./ResearchSchedule";

export interface ScheduleTotals { services: number; pax: number; revenue: number; costs: number; profit: number; days: number; sets: number }
export interface SchedulePdfData {
  season: string;
  monthLabel: string;
  dayClassScenario: string;
  assumptions: string[];
  weeks: ({ date: number; model: DayModel | null } | null)[][];
  weekTotals: ScheduleTotals[];
  viewLabel: string;
  monthTotalOptimised: ScheduleTotals;
  monthTotalUnoptimised: ScheduleTotals;
  classSummaries: { label: string; days: number; model: DayModel }[];
  classMixes: { label: string; rows: { item: string; tickets: number; net: number; share: number }[] }[];
}

const NUM = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 });
const gbp = (v: number): string => `£${NUM.format(Math.round(v))}`;
const signed = (v: number): string => `${v >= 0 ? "+" : ""}${gbp(v)}`;

const DAY_HEADS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

interface WithTable { lastAutoTable?: { finalY: number } }

export function buildSchedulePdf(data: SchedulePdfData): void {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const printedOn = new Date().toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
  });

  // Header band
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(11, 61, 46);
  doc.text(`Weekly Service Schedule — ${data.monthLabel}`, 14, 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(90, 90, 90);
  doc.text(`Season ${data.season}  ·  Timetable Studio passenger-day model  ·  calendar: ${data.viewLabel}  ·  printed ${printedOn}`, 14, 19);

  let y = 24;
  doc.setFontSize(7.5);
  for (const line of data.assumptions) {
    const wrapped = doc.splitTextToSize(line, 269) as string[];
    doc.text(wrapped, 14, y);
    y += wrapped.length * 3.2;
  }
  y += 2;

  // Calendar grid
  const dayCell = (c: { date: number; model: DayModel | null } | null): string => {
    if (!c) return "";
    if (!c.model) return `${c.date}\n(no service)`;
    const m = c.model;
    return [
      `${c.date}`,
      `${m.services} trains (${m.steamN}S/${m.dieselN}D)`,
      `Sets: ${m.sets}${m.setCodes ? ` (${m.setCodes})` : ""}`,
      `${NUM.format(Math.round(m.pax))} pax`,
      `Rev ${gbp(m.revenue)}`,
      `Cost ${gbp(m.costs)}`,
      `Profit ${signed(m.profit)}`,
    ].join("\n");
  };
  const totalCell = (t: ScheduleTotals, label: string): string => [
    label,
    `${NUM.format(t.services)} trains`,
    `${NUM.format(t.sets)} sets (peak)`,
    `${NUM.format(Math.round(t.pax))} pax`,
    `Rev ${gbp(t.revenue)}`,
    `Cost ${gbp(t.costs)}`,
    `Profit ${signed(t.profit)}`,
  ].join("\n");

  autoTable(doc, {
    head: [[...DAY_HEADS, "Week total"]],
    body: data.weeks.map((row, wi) => [
      ...row.map((c) => dayCell(c)),
      totalCell(data.weekTotals[wi], `Week ${wi + 1}`),
    ]),
    startY: y,
    margin: { left: 14, right: 14 },
    styles: { fontSize: 6.4, cellPadding: 1.2, valign: "top", textColor: [30, 30, 30] },
    headStyles: { fillColor: [11, 61, 46], textColor: 255, fontStyle: "bold", halign: "center", fontSize: 7 },
    columnStyles: { 7: { fillColor: [240, 244, 241], fontStyle: "bold" } },
  });
  y = ((doc as unknown as WithTable).lastAutoTable?.finalY ?? y) + 5;

  // Monthly overview + per-day-type modelling summary
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(11, 61, 46);
  doc.text("Monthly overview", 14, y);
  y += 2;
  autoTable(doc, {
    head: [["", "Days", "Services", "Sets", "Passengers", "Revenue", "Costs", "Profit"]],
    body: [
      ...data.classSummaries.map((c) => [
        `${c.label} (per day)`,
        String(c.days),
        `${c.model.services} (${c.model.steamN}S/${c.model.dieselN}D)`,
        `${c.model.sets}${c.model.setCodes ? ` (${c.model.setCodes})` : ""}`,
        NUM.format(Math.round(c.model.pax)),
        gbp(c.model.revenue),
        gbp(c.model.costs),
        signed(c.model.profit),
      ]),
      ...([
        ["Month total — optimised", data.monthTotalOptimised],
        ["Month total — unoptimised (baseline)", data.monthTotalUnoptimised],
      ] as const).map(([label, t]) => [
        { content: label, styles: { fontStyle: "bold" as const } },
        { content: String(t.days), styles: { fontStyle: "bold" as const } },
        { content: NUM.format(t.services), styles: { fontStyle: "bold" as const } },
        { content: `${NUM.format(t.sets)} (peak)`, styles: { fontStyle: "bold" as const } },
        { content: NUM.format(Math.round(t.pax)), styles: { fontStyle: "bold" as const } },
        { content: gbp(t.revenue), styles: { fontStyle: "bold" as const } },
        { content: gbp(t.costs), styles: { fontStyle: "bold" as const } },
        { content: signed(t.profit), styles: { fontStyle: "bold" as const } },
      ]),
      [
        { content: "Optimised vs unoptimised", styles: { fontStyle: "bold" } },
        "", "", "",
        NUM.format(Math.round(data.monthTotalOptimised.pax - data.monthTotalUnoptimised.pax)),
        signed(data.monthTotalOptimised.revenue - data.monthTotalUnoptimised.revenue),
        signed(data.monthTotalOptimised.costs - data.monthTotalUnoptimised.costs),
        signed(data.monthTotalOptimised.profit - data.monthTotalUnoptimised.profit),
      ],
    ],
    startY: y,
    margin: { left: 14, right: 14 },
    styles: { fontSize: 7.5, cellPadding: 1.4, textColor: [30, 30, 30] },
    headStyles: { fillColor: [31, 77, 63], textColor: 255, fontStyle: "bold", fontSize: 7.5 },
    columnStyles: { 0: { halign: "left" }, 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" } },
  });
  y = ((doc as unknown as WithTable).lastAutoTable?.finalY ?? y) + 6;

  // Modelled ticket splits per day type
  for (const mixTable of data.classMixes) {
    if (mixTable.rows.length === 0) continue;
    if (y > 175) { doc.addPage(); y = 14; }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(11, 61, 46);
    doc.text(`Modelled ticket split — ${mixTable.label}s (average day)`, 14, y);
    y += 2;
    autoTable(doc, {
      head: [["Ticket / item", "Modelled tickets", "Modelled net £", "% of revenue"]],
      body: mixTable.rows.map((r) => [
        r.item,
        r.tickets >= 0.5 ? NUM.format(Math.round(r.tickets)) : "—",
        gbp(r.net),
        `${(r.share * 100).toFixed(1)}%`,
      ]),
      startY: y,
      margin: { left: 14, right: 110 },
      styles: { fontSize: 7, cellPadding: 1.2, textColor: [30, 30, 30] },
      headStyles: { fillColor: [31, 77, 63], textColor: 255, fontStyle: "bold", fontSize: 7 },
      columnStyles: { 0: { halign: "left", cellWidth: 90 }, 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } },
    });
    y = ((doc as unknown as WithTable).lastAutoTable?.finalY ?? y) + 6;
  }

  doc.save(`weekly-service-schedule-${data.monthLabel.replace(/\s+/g, "-").toLowerCase()}.pdf`);
}
